import React, { forwardRef, memo, useCallback, useState } from 'react';
import classNames from 'classnames';
import { DatePicker, DatePickerProps } from 'antd';
import { PickerRef } from '@rc-component/picker/es/interface';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { datePickerDateFormat } from '@lib/enums/dateTime';
import { transformDate } from '@lib/utils/dateTimeHelpers';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import withFloatingLabel, {
  FloatingLabelProps,
} from '@lib/hocs/withFloatingLabel';
import isMobile from '@lib/utils/isMobile';
import Input from '@lib/components/Input/Input';
import { InputSizes } from '@lib/components/Input/enums';
import ExtraFooter from '@lib/components/DateTimePicker/DateRangePickerExtraFooter';
import useSelectedDate from './utils/useSelectedDate';
import getSortedDates from './utils/getSortedDates';
import styles from './DateTimePicker.module.scss';

// Ensure parsing with custom formats works regardless of app-level setup
dayjs.extend(customParseFormat);

type Props = {
  allowClear?: boolean;
  disabled?: boolean;
  format?: string;
  hasError?: boolean;
  isFocused?: boolean;
  maxDate?: DatePickerProps['maxDate'];
  minDate?: DatePickerProps['minDate'];
  name?: string;
  onChange: (v: string[]) => void;
  size?: InputSizes;
  value?: string[];
};

const DateRangePickerCommon = forwardRef(
  (props: Props & FloatingLabelProps, ref: React.ForwardedRef<PickerRef>) => {
    const {
      allowClear = true,
      disabled,
      format = datePickerDateFormat,
      hasError,
      isFocused,
      maxDate,
      minDate,
      name,
      onChange,
      setIsFocused,
      size = InputSizes.large,
      value,
    } = props;
    const [isDropdownOpened, setDropdownOpened] = useState(false);
    const [rawInput, setRawInput] = useState<string | undefined>(undefined);
    const { phone, tablet } = isMobile();
    const isMobileDevice = phone || tablet;

    const { mask, selected, inputValue, placeholder, isValueExists } =
      useSelectedDate({ value, format, isFocused });

    const shouldUseOnBlur = !(isValueExists || isDropdownOpened);
    const onBlurHandler = shouldUseOnBlur
      ? (e) => {
          const className =
            e?.relatedTarget?.className || e?.target?.className || '';
          if (className.indexOf('ant-picker') === -1) {
            setIsFocused(false);
          }
        }
      : undefined;

    const onCloseHandler = () => {
      setIsFocused(false);
      setDropdownOpened(false);
      setRawInput(undefined);
    };

    const onFocusHandler = () => {
      setDropdownOpened(true);
      setIsFocused(true);
      if (rawInput === undefined) setRawInput(inputValue);
    };

    const onChangeHandler = useCallback(
      (v: [Dayjs | null, Dayjs | null] | null) => {
        const newValue = ['', ''];
        if (v && Array.isArray(v) && v.length) {
          getSortedDates(v).forEach((date, index) => {
            if (date) {
              newValue[index] = transformDate(date.toDate(), index > 0);
            }
          });
        } else if (isMobileDevice) {
          setIsFocused(false);
        }
        onChange(newValue);
        setTimeout(() => {
          setDropdownOpened(false);
        }, 0);
        setRawInput(undefined);
      },
      [isMobileDevice, onChange, setIsFocused],
    );

    const onInputChangeHandler = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const input = e.target.value;
      if (input === '') {
        setRawInput('');
        return;
      }
      setRawInput(input);
      // Support both en dash and hyphen as range separators with optional spaces
      const [startStr = '', endStr = ''] = input
        .split(/\s*[–-]\s*/)
        .map((s) => s.trim());
      const start = dayjs(startStr, format || datePickerDateFormat, true);
      const end = dayjs(endStr, format || datePickerDateFormat, true);
      const newValue = getSortedDates([start, end]);
      if (newValue[0]?.isValid() && newValue[1]?.isValid()) {
        onChangeHandler([newValue[0], newValue[1]]);
      }
    };

    const onInputBlurHandler = () => {
      if (rawInput === '') {
        onChangeHandler(null);
        return;
      }
      // Revert to last committed range if the typed text is incomplete/invalid
      if (rawInput !== undefined && rawInput !== '') {
        const [startStr = '', endStr = ''] = rawInput
          .split(/\s*[–-]\s*/)
          .map((s) => s.trim());
        const start = dayjs(startStr, format || datePickerDateFormat, true);
        const end = dayjs(endStr, format || datePickerDateFormat, true);
        if (!(start?.isValid() && end?.isValid())) {
          setRawInput(undefined);
        }
      }
    };

    const onClearHandler = () => onChangeHandler(null);

    const className = classNames(styles.picker, {
      [styles.datePickerFilled]: isValueExists,
    });

    const allowClearValue = allowClear
      ? { clearIcon: <MaterialIcon icon="cancel" /> }
      : false;

    return (
      <div className={styles.rootDatePickerCommon}>
        <div className={styles.pickerWrapper}>
          <DatePicker.RangePicker
            allowClear={allowClearValue}
            allowEmpty
            className={className}
            disabled={disabled}
            format={datePickerDateFormat}
            inputReadOnly={isMobileDevice}
            maxDate={maxDate}
            minDate={minDate}
            name={name}
            needConfirm={false}
            onBlur={onBlurHandler}
            onChange={onChangeHandler}
            onFocus={onFocusHandler}
            open={isDropdownOpened}
            placeholder={['', '']}
            classNames={{
              popup: {
                root: styles.datePickerPopup,
              },
            }}
            ref={ref}
            renderExtraFooter={() => (
              <ExtraFooter
                onChangeHandler={onChangeHandler}
                selected={selected}
              />
            )}
            showNow={false}
            size="large"
            status={hasError ? 'error' : undefined}
            value={selected}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Input
            disabled={disabled}
            disabledIcon="calendar_today"
            hasError={hasError}
            inputMode="numeric"
            mask={mask}
            name={name}
            onBlur={onInputBlurHandler}
            onChange={onInputChangeHandler}
            onClear={onClearHandler}
            onRightIconClick={onFocusHandler}
            placeholder={placeholder}
            rightIcon="calendar_today"
            rightIconSymbolsOutlined
            size={size}
            value={rawInput !== undefined ? rawInput : inputValue}
          />
        </div>
        {isDropdownOpened && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/control-has-associated-label,jsx-a11y/no-static-element-interactions
          <div className={styles.mask} onClick={onCloseHandler} />
        )}
      </div>
    );
  },
);

export default memo(withFloatingLabel(DateRangePickerCommon));
